// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/interceptors/field-length-notice.interceptor.ts
// VERSION: v1.0.0
// DATE: 2026-10-24 — PIA (vom PO beauftragt am 24.10.2026, Scope HUGO)
// ITEM: #261 (_49) "Long input in every editor: shorten short fields, keep long text, never a 500"
//       https://devel.ceeq.it/project/tryout_c4c-debugging-the-ceeq-it_49/item/261
//
// PURPOSE: Eine Stelle für ALLE Editoren — der Nutzer erfährt, wenn das Backend beim
//          Speichern gekürzt oder abgelehnt hat (Requirements 570–573, L1/L3/L4):
//            2xx mit truncated_fields       → Hinweis "Saved with shortened fields …"
//            422 TEXT_TOO_LONG/DATA_TOO_LONG → Hinweis mit Feld und Grenze (Meldung des Backends)
//            413 BODY_TOO_LARGE             → Hinweis "The request is too large …"
//          Der Fehler wird danach unverändert weitergereicht — die Komponente behält
//          ihre eigene Fehlerbehandlung, die Eingabe bleibt im Formular stehen.
//
// NOTES:
//   ▸ Der Task-Editor zeigt Kürzungen seit #173 selbst als Zeile unter dem Formular
//     (apiNotice). Für update-task/create-task meldet dieser Interceptor deshalb nur
//     413/422, keine Kürzung — sonst stünde derselbe Hinweis zweimal da.
//   ▸ UI-Texte Englisch (PO-Regel). Feldnamen erscheinen so, wie das Backend sie nennt.
//   ▸ Eingebunden über provideHttpClient(withInterceptors([...])) in app.config.ts.
// ═══════════════════════════════════════════════════════════════════════════════

import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, tap, throwError } from 'rxjs';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH']);
const OWN_TRUNCATION_NOTICE = /\/api\/(update-task|create-task)\b/;
const LENGTH_CODES = new Set(['TEXT_TOO_LONG', 'DATA_TOO_LONG', 'BODY_TOO_LARGE']);

interface TruncatedField { field: string; max: number; original_length: number; }

export function describeTruncatedFields(fields: TruncatedField[]): string {
  const parts = fields.map(f => `${f.field} (${f.original_length} → ${f.max} characters)`);
  return `Saved with shortened fields (maximum length exceeded): ${parts.join(', ')}. `
       + 'Please review the shortened entry and adjust it if needed.';
}

export const fieldLengthNoticeInterceptor: HttpInterceptorFn = (req, next) => {
  if (!WRITE_METHODS.has(req.method)) return next(req);
  const snackBar = inject(MatSnackBar);
  const show = (text: string) => snackBar.open(text, 'Close', { duration: 12000, panelClass: 'field-length-notice' });

  return next(req).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse) || OWN_TRUNCATION_NOTICE.test(req.url)) return;
      const fields = (event.body as any)?.truncated_fields;
      if (Array.isArray(fields) && fields.length) {
        console.warn('[fieldLengthNotice] ✂️ saved with shortened fields', { url: req.url, fields });
        show(describeTruncatedFields(fields));
      }
    }),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && (err.status === 413 || err.status === 422)) {
        const code = err.error?.code;
        if (err.status === 413 || LENGTH_CODES.has(code)) {
          console.warn('[fieldLengthNotice] ⛔ rejected for length', { url: req.url, status: err.status, code, field: err.error?.field });
          show(err.error?.message
            || (err.status === 413
              ? 'The request is too large. Nothing was saved.'
              : 'A field is too long. Nothing was saved. Please shorten it and save again.'));
        }
      }
      return throwError(() => err);
    }),
  );
};
